import AppErrorBoundary from "shared/components/ErrorBoundary";
import { useDispatch, useSelector } from "react-redux";
import { GlobalProvider, Flex,Image } from "@enaep-ng/benchmark";
import { AspectRatioBox } from "@enaep-ng/benchmark-lab";
import {
  createFailureAction,
  createSetAssessmentModeAction,
  createSetExitAction,
  createSetExpectedVersion,
  createSetHomeAction,
  createSetInitialStateAction,
  createSetSwitchLanguage,
  createSetViewAction,
  createSetRelaunchInformation,
  createUpdatePreChacheValidationStatusAction,
  createUpdateServiceWorkerDetailsAction,
  createSetConfirmationModalAction
} from "shared/state/reducer";
import { useSuppressKeyboardZoom } from "shared/hooks/useSupressKeyboardZoom";
import {Header,Footer,HeaderProps, ConnectionType, ActionComponent, WarningDialogue } from "@npd-ng/adp-component-library";
import {
  AuthenticationView,
  ErrorLoadingView,
  LoadingView,
  ModeSelectionView,
  DeviceValidationResultsView,
} from "shared/views";
import UpdaterView from "shared/views/UpdaterView";
import { DeviceValidationChecks, IADPState, RelaunchInformationType, eNAEPComponents } from "shared/typings/AppStateTypes";
import { DeviceValidationLoadingView } from "shared/views/DeviceValidationLoadingView";
import { useDedupEffect } from "shared/hooks/useDedupEffect";
import { useEffect, useRef, useState } from "react";
import FailureView from "shared/views/FailureView";
import { useHeartbeatService } from "shared/hooks/useHeartBeatService";
import { ModeSwitchConfirmation } from "shared/components/ModeSwitch";
import { InternetInitialState } from "./state/initialState";
import { useInitilizeApp } from "shared/hooks/useInitilizeApp";
import { LoggPrefix, PUT_HEADER, environment, getOnlineServices, isLocalhost, getQueryParams } from "shared/util";
import { useServiceWorkerPrecacheValidation } from "shared/hooks/useServiceWorkerValidation";
import { fetchResources} from "shared/services";
import { indexDBStorage } from "shared/storage/indexDBStorage";
import { RedirectionView } from "shared/views/RedirectionView";
import { useUpdatesService } from "shared/hooks/useUpdatesService";
import RunningProccessFailureView from "shared/views/RunningProcessFailureView";

function App() {
  const state = useSelector(
    (state: { rootState: IADPState }) => state.rootState
  );
  useInitilizeApp();
  useSuppressKeyboardZoom();
  useHeartbeatService({heartBeatUrl:`${environment.resolveApiDomain(state.appState.configuration)}/${getOnlineServices(state.appState.configuration)?.heartbeatAsync}`});
  useResolveAppVersions();
  const {OnUpdateError}=useUpdatesService()
  const dispatch = useDispatch();
  const cacheValidationStarted = useRef(false);

  const selectedLanguage = {
    selectedLocale: state.appState.selectedLanguage,
    content: {
      instructions: "please select a language",
      switch: { englishVersion: "English", spanishVersion: "Espanol" },
    },
    action: (selectedLocale: "en" | "es") => {
      dispatch(createSetSwitchLanguage(selectedLocale));
    },
  };

  const [showLowBatteryPopup, setShowLowBatteryPopup] = useState(false);
  const [lowBatteryPopupShown, setLowBatteryPopupShown] = useState(false);

  useEffect(() => {
    const isLowBattery = state.deviceState.batteryState?.level < 0.3;
    const isCharging = state.deviceState.batteryState?.charging;

    if (isLowBattery && !isCharging && !lowBatteryPopupShown) {
      setShowLowBatteryPopup(true);
      setLowBatteryPopupShown(true);
    }

    // resets the popup state when the battery level is above 30% for future low battery warnings
    if (isCharging || state.deviceState.batteryState?.level >= 0.3 ) {
        setLowBatteryPopupShown(false);
        setShowLowBatteryPopup(false);
    }

  }, [state.deviceState.batteryState?.level, state.deviceState.batteryState?.charging]);

  const headerProps: HeaderProps = {
    BatteryLevel: {
      level: state.deviceState.batteryState?.level,
      state: state.deviceState.batteryState?.charging
        ? "Charging"
        : state.deviceState.batteryState?.level > 0.5
          ? "Full"
          : state.deviceState.batteryState?.level < 0.3
            ? "Danger"
            : "Half",
      v2Check: false,
      batteryWarningAlt:state.currentLanguageContent?.imageAltText.batteryWarning as string
    },
    ConnectionState:
      state.sessionState.networkConnectionState === "Connected"
        ? (["Connected"] as ConnectionType[])
        : (["N/A"] as ConnectionType[]),
    exitAction: () => {
      dispatch(createSetExitAction());
    },
    exitAlt:state.currentLanguageContent?.imageAltText.exitLogo as string,
    exitText: state.currentLanguageContent?.header?.exit || "Exit",
    homeAlt:state.currentLanguageContent?.imageAltText.homeLogo as string,
    homeText: state.currentLanguageContent?.header?.home || "",
    enLangText: state.currentLanguageContent?.header?.enLangText || "",
    enAlt:state.currentLanguageContent?.imageAltText.enLogo as string,
    esLangText: state.currentLanguageContent?.header?.esLangText || "",
    esAlt:state.currentLanguageContent?.imageAltText.esLogo as string,
    homeAction: () => {
      dispatch(createSetHomeAction());
    },
    Link: undefined,
    LanguageSwitch: selectedLanguage,
    Modal: undefined,
    closeAlt:state.currentLanguageContent?.imageAltText.closeIcon as string,
    connectionMode: {
      text: "ConnectionMode",
      routerState:
        state.sessionState.networkConnectionState === "Connected"
          ? ("Connected" as ConnectionType)
          : ("N/A" as ConnectionType),
    },
    releaseVersion: {
      title: state.currentLanguageContent?.header?.version || "",
      version: state.appState.releaseVersion || "",
      versionColors: state.appState.releaseColors,
      versionBorderColors: state.appState.releaseBorder,
    },
    homeButtonEnabled: state.sessionState.homeButtonEnabled,
    exitButtonEnabled: state.sessionState.exitButtonEnabled,
    connectedAlt:state.currentLanguageContent?.imageAltText.wifiIcon as string,
    disconnectedAlt:state.currentLanguageContent?.imageAltText.wifiOffIcon as string,
  };
  useDedupEffect(()=>{
    dispatch(createSetInitialStateAction(InternetInitialState));
    trackRelaunchInformation();
  },[])
  const accessCodeAction = ()=>{
    dispatch(createSetAssessmentModeAction("Assess"));
  };
  const verifyCodeAction = ()=>{
    dispatch(createSetAssessmentModeAction("Verify"));
  };
  useDedupEffect(() => {
    if(state.cacheValidation.validationStatus==="InProgress"){
      cacheValidationStarted.current = true;
    }
    else if(state.cacheValidation.validationStatus==="Failed"){
      OnUpdateError(state.cacheValidation.validationDetails || "An error occured trying to precache the content.")
    }
  },[cacheValidationStarted])
  
  const modeSelectionProps: ActionComponent["action"][] =[verifyCodeAction,accessCodeAction]
  const cancelConfirmationModal = () => {
    dispatch(createSetConfirmationModalAction(false));
    dispatch(createSetViewAction("AccessCodeValidationView"))
  }
  const renderCurrentView = () => {
    switch (
    state.appState.currentView
    ) {
      case "LoadingView": {
        return <LoadingView />;
      }

      case "DeviceValidationView": {
        const merged = Object.assign({}, state.appState.configuration?.deviceValidationChecks.deviceSharedChecks, 
          state.appState.configuration?.deviceValidationChecks.onlineChecks);
        const checks:DeviceValidationChecks | undefined= merged;
        return <DeviceValidationLoadingView
         checks={checks}
         healthUri={`${environment.resolveApiDomain(state.appState.configuration)}/${getOnlineServices(state.appState.configuration)?.cloudServiceHealth}`} 
         dataTransferUrl={`${environment.resolveApiDomain(state.appState.configuration)}/${getOnlineServices(state.appState.configuration)?.dataTransfer.sign}`}
         deviceMetricsURl={`${environment.resolveApiDomain(state.appState.configuration)}/${getOnlineServices(state.appState.configuration)?.postDeviceMetrics}`} putHeader={PUT_HEADER}
         cancelConfirmationModal={cancelConfirmationModal}/>;
      }
      case "RunningProccessFailureView":{
        return <RunningProccessFailureView/>
      }
      case "AccessCodeValidationView": {

        return <AuthenticationView />;
      }

      case "VerificationCodeValidationView": {

        return <AuthenticationView />;
      }

      case "ModeSelectionsView": {
        return <ModeSelectionView actions={modeSelectionProps} />;
      }
      case "FailureView": {
        return <FailureView />
      }
      case "WarningView": {
        return <ErrorLoadingView />;
      }
      case "DeviceValidationSuccessView": {

        return <DeviceValidationResultsView />;
      }

      case "DeviceValidationFailureView": {

        return <DeviceValidationResultsView />;
      }

      case "UpdaterView": {
        return <UpdaterView />;
      }
      case "RedirectionView": {
        return <RedirectionView url={`${environment.resolveENAEPDomain()}/login/index.html`} join="?"/>
      }
    }
  };
  return (
    <GlobalProvider>
      <Flex
        sx={{
          height: "calc(100vh * var(--zoom))",
          minHeight: 0,
          flexDirection: "column",
          overflow: "hidden",
          alignItems: "center",
        }}
      >
        <AspectRatioBox
          targetWidth={1366}
          targetHeight={768}
          style={{
            overflow: "hidden",
          }}
          fluid={true}
        >
          <AppErrorBoundary hasError={false}>
            <Header {...headerProps} />
            {showLowBatteryPopup && (
              <WarningDialogue
                title={
                  state.currentLanguageContent?.header?.BatteryWarning?.title ||
                  ""
                }
                subTitle={
                  state.currentLanguageContent?.header?.BatteryWarning
                    ?.subTitle || ""
                }
                closeAction={() => setShowLowBatteryPopup(false)}
                closeIconAlt={state.currentLanguageContent?.imageAltText.closeIcon as string}
              />
            )}
             <div style={{fontSize:"12px", color: "lightslategray",position: "fixed",zIndex:100,marginLeft:"calc(20px*var(--zoom))",bottom: "calc(30px * var(--zoom))"}}>
              {state.deviceState?.deviceId}</div> 
            <div>{renderCurrentView()}</div>
            {<Footer primary={true} />}
            <ModeSwitchConfirmation/>
           {state.cacheValidation.validationStatus === "InProgress" && !cacheValidationStarted.current &&
           <CachedResourceValidations />}
          </AppErrorBoundary>
        </AspectRatioBox>
      </Flex>
    </GlobalProvider>

  );

function useResolveAppVersions (){
  const appState = state.appState;
  const currentPath = window.location.href.replace("index.html", "").split("?")[0];
  const fetchBuildJsons = ()=>{
    try {
      fetchResources(`${currentPath}build.json`).then((response)=>{
        if(!response.error){
          const build = response.data;
          dispatch(createSetExpectedVersion({component:eNAEPComponents.ADP,version:build?.version || "Not Available"}))
        }
        else {
          dispatch(createSetExpectedVersion({component:eNAEPComponents.ADP,version:"Error"})) 
        }
      })
       fetchResources(`${environment.resolveENAEPDomain()}/ade/build.json`).then((response)=>{
        if(!response.error){
          const build = response.data
          dispatch(createSetExpectedVersion({component:eNAEPComponents.ADC,version:build?.version || "Not Available"}))
        }
        else {
          dispatch(createSetExpectedVersion({component:eNAEPComponents.ADC,version:"Error"})) 
        }
       })
    fetchResources(`${environment.resolveENAEPDomain()}/login/build.json`).then((response)=>{
        if(!response.error){
          const build = response.data
          dispatch(createSetExpectedVersion({component:eNAEPComponents.SCI,version:build?.version || "Not Available"}))
        }
        else {
          dispatch(createSetExpectedVersion({component:eNAEPComponents.SCI,version:"Error"})) 
        }
       
       })
    }
    catch(e){
      console.warn("Failed to fetch build.json", e)
    }
  }
 
  useDedupEffect(() => {
    if(appState.expectedADCVersion && appState.expectedADPVersion && appState.expectedSCIVersion){
      if(isLocalhost()) {
        dispatch(createUpdatePreChacheValidationStatusAction(
          {validationStatus: "Skipped",
          validationDetails: "Service Worker validation skipped"
        }))
        dispatch(createSetViewAction("UpdaterView"));
       return;
      }
      else {
        dispatch(createUpdatePreChacheValidationStatusAction(
          {validationStatus: "InProgress",
          validationDetails: "Service Worker validation in progress"
        }
        ))
      }
      
    }
  },[appState.expectedADCVersion,appState.expectedADPVersion,appState.expectedSCIVersion])
  useDedupEffect(()=>{
    if(appState.resourcesLoaded && !isLocalhost()){
        fetchBuildJsons();
    }
    else {
      dispatch(createUpdatePreChacheValidationStatusAction(
        {validationStatus: "Skipped",
        validationDetails: "Service Worker validation skipped"
      }))
      dispatch(createSetViewAction("UpdaterView"));
    }
  },[appState.appLaunchId])
  
}
function trackRelaunchInformation (){
  const queryParams = getQueryParams()
  if(queryParams.has("redirectParam")){
     dispatch(createSetRelaunchInformation(JSON.parse(queryParams.get("redirectParam") as string) as RelaunchInformationType))
  }
}

 function CachedResourceValidations() {

 
  async function resetSW() {
    let promises = [];
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const reg of regs) {
      promises.push(reg.unregister());
    }
    try {
      await Promise.all(promises).finally(() => {
        //  dispatch(actions.createUpdatePreChacheValidationStatusAction({validationStatus:"Failed",
        //  validationDetails:`Reseting service workers and reloading window`})); 
          setTimeout((()=>{
            window.location.reload();
          }),2000);
        });
    }
    catch(e){
      console.error(LoggPrefix,"[rersetSW]", e);
      dispatch(createUpdatePreChacheValidationStatusAction(
        {validationStatus: "Failed",
        validationDetails: "Service Worker validation failed while resetSW"
      }))
      dispatch(createFailureAction("UpdateError"));
    }
    
  }

  async function handleIncorrectVersion(
    version: string | undefined,
    expectedVersion: string | undefined,
    appName: eNAEPComponents
  ) {
    console.error(
      `You are running an outdated version of ${appName}! Expected version ${expectedVersion} -- you are running version ${version}!`
    );
   dispatch(createUpdatePreChacheValidationStatusAction({validationStatus:"Failed",
   validationDetails:`Device is running an outdated version of ${appName}! Expected version ${expectedVersion} -- device is running version ${version}!`})); 
   dispatch(createUpdateServiceWorkerDetailsAction({appCode:appName,cachedVersion:version}));
    const resetCount = await (await indexDBStorage.getInstance()).transactions.get("adp-session-references","resetSWCount"); 
    if(resetCount === 1){
      //await (await indexDBStorage.getInstance()).transactions.delete("adp-session-references", "resetSWCount"); 
      dispatch(
        createUpdatePreChacheValidationStatusAction(
        {validationStatus: "Failed",
        validationDetails: `Service Worker validation  failed after reset  ${{appName: appName, expectedVersion: expectedVersion,localVersion:version}} `
      }))
      dispatch(createFailureAction("UpdateError"));
    } 
    else {
      await (await indexDBStorage.getInstance()).transactions.put("adp-session-references", 1, "resetSWCount");         
      await resetSW();
    } 
  }
  async function handleSWVersion(
    version: string | undefined,
    appName: eNAEPComponents
  ) {
    console.log('handleSWVersion',{appCode:appName,cachedVersion:version});
   //dispatch(actions.createUpdateServiceWorkerDetailsAction({appCode:appName,cachedVersion:version}));
  }
  async function handlePrecacheFailure(
    expectedVersion: string | undefined,
    appName: string,
    manifestUrls: string[],
    failedUrls: string[]
  ) {
    console.error(
      `Precache failure for app ${appName}. Reaching this error means you are running app version ${expectedVersion}.\n`,
      `Precache urls were ${manifestUrls}.\n`,
      `Failed to precache the following urls: ${failedUrls}`
    );
    onError(`Precache failure for app ${appName}.
    Reaching this error means you are running app version ${expectedVersion},
    Precache urls were ${manifestUrls},Failed to precache the following urls: ${failedUrls}`); 
    //TODO - prevent going into infinite reset
    //await resetSW();
  }

  // Called when precacheFailure is set to true
  function onError(errorMessage: string) {
    console.error(`Failed with error message : \n`, errorMessage);
    dispatch(createUpdatePreChacheValidationStatusAction({validationStatus:"Failed",validationDetails:`Failed with error message : ${errorMessage}`}))
    dispatch(createFailureAction("UpdateError"));
     }

  const currentPath = window.location.href.replace("index.html", "").split("?")[0];
  const config = state.appState.configuration;
    const [isADPPrecacheComplete, isADPPrecacheFailure] = useServiceWorkerPrecacheValidation(
      `${currentPath}`,
      config?.serviceWorkersToRegister?.find((component)=>component === eNAEPComponents.ADP)? false : true,
      state.appState.expectedADPVersion,
      (version: string) => handleSWVersion(version,eNAEPComponents.ADP),
      (version: string) => handleIncorrectVersion(version, state.appState.expectedADPVersion, eNAEPComponents.ADP),
      (manifestUrls: string[], failedUrls: string[]) =>
        handlePrecacheFailure(state.appState.expectedADPVersion, eNAEPComponents.ADP, manifestUrls, failedUrls),
      onError,
      { 
        pollingIntervalMs:  config?.localCacheValidation?.pollingIntervalMs ?? 2000,
        maxRetryAttempts: config?.localCacheValidation?.maxRetryAttempts ?? 30
      }
    );
  const [isSCIPrecacheComplete, isSCIPrecacheFailure] =
    useServiceWorkerPrecacheValidation(
      `${environment.resolveENAEPDomain()}/login/`,
      config?.serviceWorkersToRegister?.find((component)=>component === eNAEPComponents.SCI)? false : true,
      state.appState.expectedSCIVersion,
      (version: string) => handleSWVersion(version,eNAEPComponents.SCI),
      (version: string) => handleIncorrectVersion(version,  state.appState.expectedSCIVersion, eNAEPComponents.SCI),
      (manifestUrls: string[], failedUrls: string[]) =>
        handlePrecacheFailure( state.appState.expectedSCIVersion,  eNAEPComponents.SCI, manifestUrls, failedUrls),
      onError,
      { 
        pollingIntervalMs:  config?.localCacheValidation?.pollingIntervalMs ?? 2000,
        maxRetryAttempts: config?.localCacheValidation?.maxRetryAttempts ?? 30
      }
    );
  const [isADCPrecacheComplete, isADCPrecacheFailure] =
    useServiceWorkerPrecacheValidation(
      `${environment.resolveENAEPDomain()}/ade/`,
      config?.serviceWorkersToRegister?.find((component)=>component === eNAEPComponents.ADC)? false : true,
      state.appState.expectedADCVersion,
      (version: string) => handleSWVersion(version,eNAEPComponents.ADC),
      (version: string) => handleIncorrectVersion(version, state.appState.expectedADCVersion, eNAEPComponents.ADC),
      (manifestUrls: string[], failedUrls: string[]) =>
        handlePrecacheFailure(state.appState.expectedADCVersion, eNAEPComponents.ADC, manifestUrls, failedUrls),
      onError,
      { 
        pollingIntervalMs:  config?.localCacheValidation?.pollingIntervalMs ?? 2000,
        maxRetryAttempts: config?.localCacheValidation?.maxRetryAttempts ?? 30
      }
    );   
    useDedupEffect(() => {
    if (isADPPrecacheComplete && isSCIPrecacheComplete && isADCPrecacheComplete
    ) {
      (async () => {
        await (await indexDBStorage.getInstance()).transactions.delete("adp-session-references","swRegistrationAttempt"); 
        await (await indexDBStorage.getInstance()).transactions.delete("adp-session-references", "resetSWCount"); 
      })();
      dispatch(createUpdatePreChacheValidationStatusAction(
        {validationStatus: "Complete",
        validationDetails: "Service Worker validation complete"
      }))
      dispatch(createSetViewAction("UpdaterView"))
    }
    if (isADPPrecacheFailure || isSCIPrecacheFailure || isADCPrecacheFailure) {  
     dispatch(createUpdatePreChacheValidationStatusAction(
      {validationStatus: "Failed",
      validationDetails: "Service Worker validation  failed"
    }))
     dispatch(createFailureAction("UpdateError"));
     // state.sessionState.networkConnectionState!=="online"  ?   dispatch(actions.createWarningAction("NoInternetConnectionDetected")) : dispatch(actions.createSetViewAction("LoadingErrroView"));
    }   
  }, [
    isADPPrecacheComplete,
    isSCIPrecacheComplete,
    isADCPrecacheComplete,
    isADPPrecacheFailure,
    isSCIPrecacheFailure,
    isADCPrecacheFailure,
  ]);
  return null;
}
}

export default App;


